import { useTranslation } from 'react-i18next';
import { Button, Menu, SVGIcon } from '@asset-metrix/portal-ui';
import { Menu as MantineMenu } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';

import { useLoginInfoQuery } from '../../api/portalGateway/loginInfo';
import { COLOR_SCHEME_STORAGE_KEY } from '../../utils/constants';

import styles from './userMenu.module.scss';

export type UserMenuProps = {
  onLogout?: () => void;
  onChangePassword?: () => void;
  portalTarget?: string | HTMLElement;
};

export function UserMenu({ onLogout, onChangePassword }: UserMenuProps) {
  const { t } = useTranslation();
  const { data } = useLoginInfoQuery();

  const [colorScheme, setColorScheme] = useLocalStorage<
    'dark' | 'light' | undefined
  >({
    getInitialValueInEffect: false,
    key: COLOR_SCHEME_STORAGE_KEY,
  });

  const logoutItem = t('app.logout');
  const changePasswordItem = t('app.changePassword');
  const changeThemeItem =
    colorScheme === 'dark'
      ? t('app.switchToLightTheme')
      : t('app.switchToDarkTheme');

  // Only add menu items, if click handlers are provided
  const menuItems: string[] = [
    onChangePassword ? changePasswordItem : undefined,
    onLogout ? logoutItem : undefined,
  ].filter((item) => item != null);

  if (data?.theme?.userCanSwitchTheme) {
    menuItems.unshift(changeThemeItem);
  }

  const onSelect = async (selectedItem: string) => {
    if (selectedItem === logoutItem) {
      await onLogout?.();
    } else if (selectedItem === changePasswordItem) {
      await onChangePassword?.();
    } else if (selectedItem === changeThemeItem) {
      await setColorScheme((current) =>
        current === 'dark' ? 'light' : 'dark'
      );
    }
  };

  return (
    <Menu
      menuTarget={<Button icon={<SVGIcon name="user" />} variant="secondary" />}
      menuDropdown={menuItems.map((item) => (
        <MantineMenu.Item
          className={styles['menu-item']}
          key={item}
          onClick={() => onSelect(item)}
        >
          {item}
        </MantineMenu.Item>
      ))}
      width={238}
    />
  );
}
